import { Request, UnaryResponse, ClientReadableStream } from 'grpc-web';
import { SERVER_DEBUG_LOG_ENABLED } from '@utils/logging-policy';
import { logServerError, logServerSuccess, nextServerLogId, toLogRequestObject } from '@utils/server-log';

/** Diagnostic getters and console failures must never change transport behavior. */
export async function invokeWithBackendLog<Req, Res>(
  request: Request<Req, Res>,
  invoker: (request: Request<Req, Res>) => Promise<UnaryResponse<Req, Res>>,
  attempt: number,
): Promise<UnaryResponse<Req, Res>> {
  if (!SERVER_DEBUG_LOG_ENABLED) return invoker(request);
  const startedAt = Date.now();
  const id = nextServerLogId();
  let method = 'RPC';
  let payload: unknown;
  try {
    method = request.getMethodDescriptor().getName();
    payload = toLogRequestObject(request.getRequestMessage(), method);
  } catch { /* Best-effort snapshot. */ }
  try {
    const response = await invoker(request);
    try {
      logServerSuccess('BACKEND gRPC', method, response.getResponseMessage(), {
        id, request: payload, durationMs: Date.now() - startedAt,
        transport: { attempt, status: response.getStatus(), metadata: response.getMetadata() },
      });
    } catch { /* Preserve original successful response. */ }
    return response;
  } catch (error: unknown) {
    logServerError('BACKEND gRPC', method, error, {
      id, request: payload, durationMs: Date.now() - startedAt, transport: { attempt },
    });
    throw error;
  }
}

/** Logging-only auth client interceptor; no TokenService dependency or DI cycle. */
export class BackendLogInterceptor {
  intercept = <Req, Res>(request: Request<Req, Res>, invoker: (request: Request<Req, Res>) => Promise<UnaryResponse<Req, Res>>) =>
    invokeWithBackendLog(request, invoker, 1);
}

/** Observes the original stream without changing cancellation or retry behavior. */
export function invokeStreamWithBackendLog<Req, Res>(
  request: Request<Req, Res>,
  invoker: (request: Request<Req, Res>) => ClientReadableStream<Res>,
  attempt: number,
): ClientReadableStream<Res> {
  if (!SERVER_DEBUG_LOG_ENABLED) return invoker(request);
  const id = nextServerLogId();
  const startedAt = Date.now();
  let method = 'Stream RPC';
  let payload: unknown;
  try {
    method = request.getMethodDescriptor().getName();
    payload = toLogRequestObject(request.getRequestMessage(), method);
  } catch { /* Best-effort snapshot. */ }
  const options = () => ({ id, request: payload, durationMs: Date.now() - startedAt, transport: { attempt } });
  try {
    const stream = invoker(request);
    try {
      stream.on('data', response => logServerSuccess('BACKEND gRPC STREAM', method, response, options()));
      stream.on('error', error => logServerError('BACKEND gRPC STREAM', method, error, options()));
    } catch { /* Diagnostic listeners must not affect stream creation. */ }
    return stream;
  } catch (error: unknown) {
    logServerError('BACKEND gRPC STREAM', method, error, options());
    throw error;
  }
}
